import React, { useState } from "react";
import * as api from "../api";
import TRANSLATIONS from "../translations";
import { C, R, F, monoRef } from "../theme";
import { Icon } from "../icons";
import { Btn, Card, Input, Callout } from "./UI";

function Login({ setView, setUser }) {
  const t = TRANSLATIONS.fr;
  const [loginVal,   setLoginVal]   = useState("");
  const [pass,       setPass]       = useState("");
  const [error,      setError]      = useState("");
  const [loading,    setLoading]    = useState(false);
  const [mfaStep,    setMfaStep]    = useState(false);
  const [tempToken,  setTempToken]  = useState("");
  const [totpCode,   setTotpCode]   = useState("");
  const [attempts,   setAttempts]   = useState(0);

  function redirectUser(user) {
    if (!user || !user.role) {
      setError("Erreur de session. Veuillez réessayer.");
      return;
    }
    setUser(user);
    if (user.role === "admin")        setView("admin");
    else if (user.role === "manager") setView("mgr");
    else                              setView("col_list");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.login(loginVal, pass);
      if (res.mfa_required) {
        setTempToken(res.temp_token);
        setMfaStep(true);
      } else {
        redirectUser(res.user);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleMFA(e) {
    e.preventDefault();
    if (!totpCode || totpCode.replace(/\s/g, "").length !== 6) {
      setError("Entrez le code à 6 chiffres"); return;
    }
    setError(""); setLoading(true);
    try {
      const res = await api.verifyMFA(tempToken, totpCode.replace(/\s/g, ""));
      redirectUser(res.user);
    } catch (err) {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      setTotpCode("");
      if (newAttempts >= 5) {
        setError("Trop de tentatives. Reconnectez-vous.");
        setMfaStep(false); setTempToken(""); setAttempts(0);
      } else {
        setError(`${err.message} (${5 - newAttempts} tentative(s) restante(s))`);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
    }}>
      <div className="animate-fadeInUp" style={{ width: "100%", maxWidth: 400 }}>
        <Card style={{ padding: 24 }}>
          {/* ─── En-tête ───────────────────────────────────────────────── */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 18 }}>
              <span style={{
                width: 30, height: 30, borderRadius: R.md, flexShrink: 0,
                background: C.navy, color: C.inkOn,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <Icon name={mfaStep ? "shield" : "washing"} size={17} color={C.inkOn} />
              </span>
              <span style={{
                fontSize: F.size.lg, fontWeight: F.weight.semi,
                color: C.ink, letterSpacing: "-.01em",
              }}>
                Setal Express
              </span>
            </div>

            <h1 style={{
              fontSize: F.size["2xl"], fontWeight: F.weight.semi,
              color: C.ink, letterSpacing: "-.02em",
            }}>
              {t.loginTitle}
            </h1>
            <p style={{ fontSize: F.size.base, color: C.inkMuted, marginTop: 3 }}>
              {mfaStep ? "Vérification en deux étapes" : t.loginSubtitle}
            </p>
          </div>

          {!mfaStep ? (
            <form onSubmit={handleSubmit}>
              <Input
                label={t.loginId} icon="user"
                value={loginVal} onChange={setLoginVal}
                placeholder="ex : collecteur1" required
              />
              <Input
                label={t.loginPass} icon="lock" type="password"
                value={pass} onChange={setPass}
                placeholder="••••••••" required
              />

              {error && (
                <Callout tone="danger" style={{ marginBottom: 14 }}>{error}</Callout>
              )}

              <Btn fullWidth disabled={loading} loading={loading}>
                {loading ? "Connexion…" : t.loginBtn}
              </Btn>
            </form>
          ) : (
            <form onSubmit={handleMFA}>
              <Callout icon="mobile" style={{ marginBottom: 16 }}>
                Ouvrez <strong>Google Authenticator</strong> et entrez le code à 6 chiffres
                pour <strong>Setal Express</strong>.
              </Callout>

              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor="totp-code"
                  style={{
                    display: "block", fontWeight: F.weight.medium, marginBottom: 5,
                    fontSize: F.size.base, color: C.inkBody,
                  }}
                >
                  Code d'authentification
                </label>
                <input
                  id="totp-code"
                  className="field"
                  value={totpCode}
                  onChange={e => setTotpCode(e.target.value.replace(/[^0-9\s]/g, ""))}
                  placeholder="000 000" maxLength={7} autoFocus
                  inputMode="numeric"
                  style={{
                    ...monoRef,
                    padding: "12px 11px",
                    fontSize: F.size["2xl"],
                    fontWeight: F.weight.semi,
                    textAlign: "center",
                    letterSpacing: 6,
                  }}
                />
                <p style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                  fontSize: F.size.sm, color: C.inkFaint, marginTop: 6,
                }}>
                  <Icon name="timer" size={12} />
                  Le code expire dans 30 secondes
                </p>
              </div>

              {error && (
                <Callout tone="danger" style={{ marginBottom: 14 }}>{error}</Callout>
              )}

              <Btn
                fullWidth
                disabled={loading || totpCode.replace(/\s/g, "").length !== 6}
                loading={loading}
                style={{ marginBottom: 8 }}
              >
                {loading ? "Vérification…" : "Vérifier"}
              </Btn>

              <Btn
                variant="subtle" fullWidth small icon="back"
                onClick={() => { setMfaStep(false); setError(""); setTotpCode(""); setAttempts(0); }}
              >
                Retour à la connexion
              </Btn>
            </form>
          )}

          {!mfaStep && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
              <Btn variant="subtle" fullWidth small icon="back" onClick={() => setView("landing")}>
                {t.backSite}
              </Btn>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

export default Login;
